import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import api, { getAuthToken } from '../services/api';

// Action Types
const BILLING_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_INVOICES: 'SET_INVOICES',
  SET_INVOICE_DETAIL: 'SET_INVOICE_DETAIL',
  SET_FILTER_OPTIONS: 'SET_FILTER_OPTIONS',
  SET_SUMMARY: 'SET_SUMMARY',
  CLEAR_ERROR: 'CLEAR_ERROR',
  RESET_STATE: 'RESET_STATE'
};

// Initial State
const initialState = {
  invoices: [],
  invoiceDetail: null,
  filterOptions: {
    invoice_statuses: {},
    payment_statuses: {},
    payment_modes: {}
  },
  summary: {
    total_invoices: 0,
    total_amount: 0,
    total_paid: 0,
    total_due: 0,
    pending_invoices: 0
  },
  loading: false,
  error: null
};

// Reducer
const billingReducer = (state, action) => {
  switch (action.type) {
    case BILLING_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    
    case BILLING_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    
    case BILLING_ACTIONS.SET_INVOICES:
      return {
        ...state,
        invoices: action.payload.data || action.payload.invoices || [],
        summary: action.payload.summary || {},
        loading: false,
        error: null
      };
    
    case BILLING_ACTIONS.SET_INVOICE_DETAIL:
      return {
        ...state,
        invoiceDetail: action.payload.data || action.payload,
        loading: false,
        error: null
      };
    
    case BILLING_ACTIONS.SET_FILTER_OPTIONS:
      return {
        ...state,
        filterOptions: action.payload.data || action.payload,
        loading: false,
        error: null
      };
    
    case BILLING_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    case BILLING_ACTIONS.RESET_STATE:
      return initialState;
    
    default:
      return state;
  }
};

// Create Context
const BillingAndInvoiceContext = createContext();

// Provider Component
export const BillingAndInvoiceProvider = ({ children }) => {
  const [state, dispatch] = useReducer(billingReducer, initialState);

  // Helper function to handle API calls
  const apiCall = async (apiFunction, actionType, errorMessage) => {
    try {
      dispatch({ type: BILLING_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: BILLING_ACTIONS.CLEAR_ERROR });
      
      const response = await apiFunction();
      
      // Handle both success and error responses from the API
      if (response.data && response.data.success) {
        dispatch({ type: actionType, payload: response.data });
        return response.data;
      } else {
        // Handle case where API returns data but success is false
        const errorMsg = response.data?.error || errorMessage;
        console.warn('API returned success=false:', errorMsg);
        // Set empty data instead of throwing error
        dispatch({ type: actionType, payload: { data: [], summary: {} } });
        return { data: [], summary: {} };
      }
    } catch (error) {
      // Handle authentication errors
      if (error.isAuthError) {
        console.warn('Authentication error:', error.message);
        dispatch({ type: actionType, payload: { data: [], summary: {} } });
        return { data: [], summary: {} };
      }
      
      // Handle network errors
      if (error.code === 'NETWORK_ERROR' || !error.response) {
        console.warn('Network error:', error.message);
        dispatch({ type: actionType, payload: { data: [], summary: {} } });
        return { data: [], summary: {} };
      }
      
      // Handle 404 errors specifically
      if (error.response?.status === 404) {
        console.warn('API endpoint not found:', error.message);
        dispatch({ type: actionType, payload: { data: [], summary: {} } });
        return { data: [], summary: {} };
      }
      
      // Handle all other API errors (including 500, 400, etc.)
      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || errorMessage;
      console.warn('API error (status:', error.response?.status, '):', errorMsg);
      
      // Set empty data instead of throwing error for all non-200 responses
      dispatch({ type: actionType, payload: { data: [], summary: {} } });
      return { data: [], summary: {} };
    }
  };

  // API Functions
  const fetchInvoices = async () => {
    return apiCall(
      () => api.get('/billing/invoices/'),
      BILLING_ACTIONS.SET_INVOICES,
      'Failed to fetch invoices'
    );
  };

  const fetchInvoiceDetail = async (invoiceId) => {
    return apiCall(
      () => api.get(`/billing/invoices/${invoiceId}/`),
      BILLING_ACTIONS.SET_INVOICE_DETAIL,
      'Failed to fetch invoice details'
    );
  };

  const fetchFilterOptions = async () => {
    return apiCall(
      () => api.get('/billing/filters/'),
      BILLING_ACTIONS.SET_FILTER_OPTIONS,
      'Failed to fetch filter options'
    );
  };

  /**
   * FIXED: Step 1 - Get pricing units for UI
   * 
   * GET /billing/pricing-units/<action>/
   * 
   * Response structure:
   * {
   *   success: true,
   *   action: 'upgrade' | 'add_user' | 'add_credits',
   *   data: {  // IMPORTANT: Units are in 'data' field, NOT 'units'
   *     current_active_user: number,
   *     per_user_cost: number,
   *     per_credit_cost: number,
   *     base_voice_credits_per_cycle: number,
   *     tax_rate: number,
   *     currency: string,
   *     ... other fields
   *   }
   * }
   * 
   * @param {Object} params - { action?: string, seats?: number }
   * @returns {Promise<Object>} Response with data field containing units
   */
  const getProformaUnits = async (action) => {
    try {
      const response = await api.get(`/billing/pricing-units/${action}/`);
      console.log('getProformaUnits response:', response.data);
      return response.data;
    } catch (error) {
      console.error('getProformaUnits error:', error);
      throw error;
    }
  };

  /**
   * FIXED: Step 2 - Get proforma invoice preview
   * 
   * POST /billing/proforma/
   * 
   * Request body:
   * - add_user: { action: 'add_user', seats: N }
   * - add_credits: { action: 'add_credits', credits: N }
   * - upgrade: { action: 'upgrade', slots: N, billing_cycle: 3|6|12, credits: N }
   * 
   * Response structure:
   * {
   *   success: true,
   *   action: string,
   *   preview: {
   *     line_items: [
   *       {
   *         description: string,
   *         quantity: number,
   *         unit_price: number,
   *         amount: number,
   *         total_amount: number,  // IMPORTANT: Frontend expects this
   *         discount?: number,
   *         worth?: number
   *       }
   *     ],
   *     subtotal: number,
   *     taxable_amount: number,
   *     tax: number,
   *     tax_amount: number,  // Same as tax
   *     total: number,
   *     discount: number,
   *     savings: number,
   *     free_credits_allotted: number,
   *     worth_of_free_credits: number,
   *     currency: string
   *   }
   * }
   * 
   * @param {Object} params - Request payload with action and action-specific params
   * @returns {Promise<Object>} Response with preview field
   */
  const getProformaPreview = async (params) => {
    try {
      const response = await api.post('/billing/proforma/', params, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      console.error('getProformaPreview error:', error);
      throw error;
    }
  };

  /**
   * Step 3 - Confirm and create invoice with payment link.
   * Same API used by: Subscription (add_user, add_credits) and UpgradeToProPage (upgrade).
   * Frontend uses invoice.payment_link / payment_url in Razorpay modal for Pay Now.
   *
   * POST /billing/confirm/
   *
   * Request body (flat structure, no nesting):
   * - add_user: { action: 'add_user', slots: N }
   * - add_credits: { action: 'add_credits', credits: N }
   * - upgrade: { action: 'upgrade', plan_id: N, total_user: N, additional_credits: N, billing_cycle: 3|6|12 }
   *
   * Response structure:
   * {
   *   success: true,
   *   action: string,
   *   invoice: {
   *     id: number,
   *     invoice_number: string,
   *     payment_link: string,  // Used in Razorpay modal
   *     total: number,
   *     status: string,
   *     ...
   *   },
   *   payment_url?: string  // Fallback payment URL
   * }
   *
   * @param {Object} payload - Flat request payload with action and params
   * @returns {Promise<Object>} Response with invoice and payment_link
   */
  const confirmBilling = async (payload) => {
    try {
      const response = await api.post('/billing/confirm/', payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      console.error('confirmBilling error:', error);
      throw error;
    }
  };

  /**
   * Get invoice payment status (single request).
   * Used by polling hook and one-off checks. Auth token is attached by api interceptor.
   *
   * GET /billing/invoice-status/?invoice_number=XXX
   *
   * On 2xx: { success: true, paid: boolean, status?: string, invoice_number?: string }
   * On 4xx/5xx or network error: { success: false, paid: false, statusCode?: number }
   *
   * @param {string} invoiceNumber - Invoice number to check
   * @returns {Promise<Object>} Status response
   */
  const getInvoicePaymentStatus = async (invoiceNumber) => {
    if (!invoiceNumber) return { success: false, paid: false };
    try {
      const response = await api.get('/billing/invoice-status/', {
        params: { invoice_number: invoiceNumber },
      });
      return response.data || { success: false, paid: false };
    } catch (err) {
      const statusCode = err.response?.status;
      if (statusCode !== undefined) {
        console.warn('getInvoicePaymentStatus non-2xx:', statusCode, err.message);
      } else {
        console.error('getInvoicePaymentStatus error:', err);
      }
      return {
        success: false,
        paid: false,
        statusCode,
      };
    }
  };

  // Context Value
  const value = {
    // State
    ...state,
    
    // Actions
    fetchInvoices,
    fetchInvoiceDetail,
    fetchFilterOptions,
    getProformaUnits,
    getProformaPreview,
    confirmBilling,
    getInvoicePaymentStatus,
    
    // Helper Functions
    clearError: () => dispatch({ type: BILLING_ACTIONS.CLEAR_ERROR }),
    resetState: () => dispatch({ type: BILLING_ACTIONS.RESET_STATE }),
    
    // Computed Values
    hasInvoices: state.invoices.length > 0,
    hasPendingInvoices: state.summary.pending_invoices > 0,
    isOverdue: state.summary.total_due > 0,
    
    // Summary Helpers
    getFormattedSummary: () => ({
      total_invoices: state.summary.total_invoices,
      total_amount: state.summary.total_amount,
      total_paid: state.summary.total_paid,
      total_due: state.summary.total_due,
      pending_invoices: state.summary.pending_invoices
    })
  };

  return (
    <BillingAndInvoiceContext.Provider value={value}>
      {children}
    </BillingAndInvoiceContext.Provider>
  );
};

// Custom Hook
export const useBillingAndInvoice = () => {
  const context = useContext(BillingAndInvoiceContext);
  if (!context) {
    throw new Error('useBillingAndInvoice must be used within a BillingAndInvoiceProvider');
  }
  return context;
};

const POLL_INTERVAL_MS = 5000;

/**
 * Poll invoice payment status every 5s. Stops when:
 * - User is not authenticated (no token)
 * - API returns non-2xx or network error
 * - Invoice reaches terminal state (paid, cancelled)
 * - Component unmounts
 *
 * @param {string} invoiceNumber - Invoice number to poll
 * @param {Object} options - { enabled?: boolean, onPaid?: () => void, onCancelled?: () => void, onError?: () => void }
 * @returns {{ stop: () => void }} - Call stop() to clear interval manually
 */
export const useInvoicePaymentPolling = (invoiceNumber, options = {}) => {
  const { enabled = true, onPaid, onCancelled, onError } = options;
  const { getInvoicePaymentStatus } = useBillingAndInvoice();
  const intervalRef = useRef(null);
  const onPaidRef = useRef(onPaid);
  const onCancelledRef = useRef(onCancelled);
  const onErrorRef = useRef(onError);
  onPaidRef.current = onPaid;
  onCancelledRef.current = onCancelled;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!enabled || !invoiceNumber) return;
    if (!getAuthToken()) return;

    const clearIntervalSafe = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const poll = async () => {
      if (!getAuthToken()) {
        clearIntervalSafe();
        return;
      }
      try {
        const res = await getInvoicePaymentStatus(invoiceNumber);
        if (!res || res.success === false) {
          clearIntervalSafe();
          onErrorRef.current?.();
          return;
        }
        if (res.paid === true || res.status === 'PD') {
          clearIntervalSafe();
          onPaidRef.current?.();
          return;
        }
        if (res.status === 'CN') {
          clearIntervalSafe();
          onCancelledRef.current?.();
          return;
        }
      } catch (err) {
        clearIntervalSafe();
        onErrorRef.current?.();
      }
    };

    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearIntervalSafe();
  }, [invoiceNumber, enabled, getInvoicePaymentStatus]);

  return {
    stop: () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    },
  };
};

// Export Action Types for external use
export { BILLING_ACTIONS };